"use client";
import React, { useRef, useEffect, useState } from "react";
import { motion, useInView } from "motion/react";
import BorderWrapper from "@/components/ui/border-wrapper";
import AnimatedCard from "@/components/ui/animated-card";
import Image from "next/image";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { useLocale, useTranslations } from "next-intl";

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

// stats and overviewItems moved inside component for i18n access

function AnimatedCounter({ target, suffix, trigger }: { target: number; suffix: string; trigger: boolean }) {
  const [count, setCount] = useState(0);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!trigger) {
      hasRun.current = false;
      setCount(0);
      return;
    }
    if (hasRun.current) return;
    hasRun.current = true;

    let start: number | null = null;
    const duration = 1400;

    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      // ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [trigger, target]);

  return (
    <>
      {count}
      {suffix}
    </>
  );
}

const AboutStory = () => {
  const t = useTranslations("about");
  const locale = useLocale();
  const tA11y = useTranslations("accessibility");
  const stats = [
    { value: "10+", numeric: 10, suffix: "+", label: t("story.stats.experience") },
    { value: "3",   numeric: 3,  suffix: "",  label: t("story.stats.continents") },
    { value: "99.9%", numeric: 99, suffix: ".9%", label: t("story.stats.uptime") },
    { value: "24h", numeric: 24, suffix: "h", label: t("story.stats.responseTime") },
  ];
  const overviewItems = [
    { label: t("story.info.founded"), value: t("story.info.foundedValue") },
    { label: t("story.info.offices"), value: t("story.info.officesValue") },
    { label: t("story.info.focus"), value: t("story.info.focusValue") },
    { label: t("story.info.type"), value: t("story.info.typeValue") },
  ];
  const sectionRef = useRef<HTMLDivElement>(null);
  const visionRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -80px 0px" });
  const visionInView = useInView(visionRef, { once: true, margin: "0px 0px -80px 0px" });
  const statsInView = useInView(statsRef, { once: true, margin: "0px 0px -80px 0px" });

  return (
    <>
      {/* ── TEAM & CULTURE ── */}
      <section
        id="story"
        className="section pt-[60px] lg:pt-[140px] pb-[60px] lg:pb-[140px] relative"
      >
        <div
          className="absolute right-[-10%] top-[20%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.12)_0%,_rgba(98,42,173,0.08)_50%,_rgba(34,12,81,0)_100%)] rounded-full blur-[100px] pointer-events-none animate-shape-pulse"
          />

        <div className="container relative z-10">
          <motion.div
            ref={sectionRef}
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "show" : "hidden"}
            className="grid lg:grid-cols-2 gap-[40px] lg:gap-[70px] items-start"
          >
            {/* Left */}
            <div className="space-y-[28px] md:max-w-[560px] lg:max-w-none">
              <motion.div variants={itemVariants}>
                <h2 className="text-[28px] md:text-[44px] lg:text-[52px] font-semibold leading-[1.1]">
                  {t("story.titleLine1")}<br />{t("story.titleLine2")}
                  <span className="block text-foreground/30 my-[4px]">—</span>
                  <span className="text-[#4a3290]">{t("story.titleAccent")}</span>
                </h2>
              </motion.div>

              <motion.p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90" variants={itemVariants}>
                {t("story.description")}
              </motion.p>

              <motion.p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90" variants={itemVariants}>
                {t("story.description2")}
              </motion.p>

              {/* Tags */}
              <motion.div className="flex flex-wrap gap-[10px]" variants={itemVariants}>
                {[t("story.tags.0"), t("story.tags.1"), t("story.tags.2"), t("story.tags.3")].map((tag) => (
                  <span
                    key={tag}
                    className="px-[14px] py-[7px] rounded-full text-[13px] font-light border border-[#353353] bg-[#121124] text-foreground/90 transition-colors duration-200 hover:border-theme/40 hover:text-foreground/90 cursor-default"
                  >
                    {tag}
                  </span>
                ))}
              </motion.div>
            </div>

            {/* Right */}
            <div className="space-y-[14px]">
              {/* Image card */}
              <motion.div variants={itemVariants}>
                <AnimatedCard
                  className="rounded-[18px] lg:rounded-[22px]"
                  circleClassName="w-[150px] h-[150px] blur-[90px]"
                >
                  <BorderWrapper
                    wrapperClass="rounded-[18px] lg:rounded-[22px]"
                    className="rounded-[18px] lg:rounded-[22px] bg-gradient-to-b from-[#121124] to-[#0e0821] overflow-hidden relative"
                  >
                    <div className="relative w-full h-[280px] lg:h-[340px]">
                      <Image
                        src={`/assets/photos/growth-${locale}.webp`}
                        alt="Flyingcode team"
                        width={1092}
                        height={674}
                        className="absolute inset-0 w-full h-full object-cover mix-blend-plus-lighter opacity-70"
                      />
                    </div>
                    <div className="absolute bottom-[14px] left-[14px] px-[12px] py-[7px] rounded-[10px] bg-[#0e0821]/80 backdrop-blur-md border border-white/10 text-[12px] font-light text-foreground/90 flex items-center gap-[6px]">
                      {t("story.location")}
                    </div>
                  </BorderWrapper>
                </AnimatedCard>
              </motion.div>

              {/* Stats with counter animation */}
              <motion.div
                ref={statsRef}
                className="grid grid-cols-2 gap-[12px]"
                variants={itemVariants}
              >
                {stats.map((stat, i) => (
                  <AnimatedCard
                    key={i}
                    className="rounded-[14px] lg:rounded-[18px]"
                    circleClassName="w-[100px] h-[100px] blur-[60px]"
                    animationDelay={i * 0.06}
                  >
                    <BorderWrapper
                      wrapperClass="rounded-[14px] lg:rounded-[18px] h-full"
                      className="rounded-[14px] lg:rounded-[18px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] px-[20px] py-[22px] relative"
                    >
                      <div
                        className="absolute -top-[20px] -right-[20px] w-[80px] h-[80px] bg-theme/15 rounded-full blur-[30px] pointer-events-none animate-shape-pulse"
                        />
                      <div className="text-[34px] lg:text-[42px] font-semibold leading-[1] text-[#4a3290] mb-[5px]">
                        <AnimatedCounter
                          target={stat.numeric}
                          suffix={stat.suffix}
                          trigger={statsInView}
                        />
                      </div>
                      <div className="text-[12px] font-light text-foreground/85">
                        {stat.label}
                      </div>
                    </BorderWrapper>
                  </AnimatedCard>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── OVERVIEW + MISSION ── */}
      <section className="section pb-[50px] lg:pb-[100px] relative">
        <div className="container relative z-10">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "show" : "hidden"}
          >
            {/* Mission strip */}
            <motion.div variants={itemVariants} className="mb-[14px]">
              <AnimatedCard
                className="rounded-[18px] lg:rounded-[22px]"
                circleClassName="w-[180px] h-[180px] blur-[100px]"
              >
                <BorderWrapper
                  wrapperClass="rounded-[18px] lg:rounded-[22px]"
                  className="rounded-[18px] lg:rounded-[22px] bg-gradient-to-b from-[#121124] to-[#0e0821] px-[32px] lg:px-[50px] py-[28px] lg:py-[36px] relative overflow-hidden"
                >
                  <div
                    className="absolute top-0 right-0 w-[300px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                    />
                  <div className="relative z-10 flex flex-col lg:flex-row lg:items-center gap-[16px] lg:gap-[40px]">
                    <div className="flex-shrink-0">
                      <div className="text-[11px] font-medium text-theme/60 uppercase tracking-[0.18em] mb-[8px]">{t("story.ourMission")}</div>
                      <p className="text-[18px] lg:text-[22px] font-semibold leading-[1.35] max-w-[540px]">
                        {t("story.missionText")}
                      </p>
                    </div>
                    <div className="hidden md:block w-[1px] self-stretch bg-white/8 mx-[10px]" />
                    <div className="grid grid-cols-2 lg:grid-cols-1 gap-[10px] lg:gap-[8px] flex-shrink-0 lg:min-w-[260px]">
                      {overviewItems.map((item) => (
                        <div key={item.label}>
                          <div className="text-[11px] font-medium text-foreground/50 uppercase tracking-[0.12em] mb-[2px]">{item.label}</div>
                          <div className="text-[13px] font-light text-foreground/90">{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </BorderWrapper>
              </AnimatedCard>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── BUILT IN NEPAL / OUR VISION ── */}
      <section className="section pb-[60px] lg:pb-[140px] relative">
        <div
          className="absolute left-[-5%] top-[30%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.1)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[100px] pointer-events-none animate-shape-pulse"
          />

        <div className="container relative z-10">
          <motion.div
            ref={visionRef}
            variants={containerVariants}
            initial="hidden"
            animate={visionInView ? "show" : "hidden"}
          >
            {/* MOBILE + TABLET CAROUSEL */}
            <div className="lg:hidden">
              <Carousel aria-label={tA11y("ourStory")} opts={{ align: "center", loop: false }} className="w-full [&>div]:!overflow-visible">
                <CarouselContent className="-ml-2 md:-ml-[32px]">
                  <CarouselItem className="pl-2 md:pl-[32px] basis-[85%] md:basis-3/4">
                    <AnimatedCard className="rounded-[18px] lg:rounded-[22px] h-full" circleClassName="w-[150px] h-[150px] blur-[90px]">
                      <BorderWrapper wrapperClass="rounded-[18px] lg:rounded-[22px] h-full" className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[32px] lg:p-[44px] relative">
                        <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse" />
                        <Image src="/assets/illustrations/flyingcode-shield.svg" alt="Global collaboration" width={86} height={111} className="mb-[24px] w-auto h-[76px] mix-blend-plus-lighter mx-auto" />
                        <h3 className="text-[22px] lg:text-[28px] font-semibold leading-[1.2] mb-[14px] text-center">{t("story.globalTeamTitle")}</h3>
                        <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 text-center">{t("story.globalTeamDescription")}</p>
                      </BorderWrapper>
                    </AnimatedCard>
                  </CarouselItem>
                  <CarouselItem className="pl-2 md:pl-[32px] basis-[85%] md:basis-3/4">
                    <AnimatedCard className="rounded-[18px] lg:rounded-[22px] h-full" circleClassName="w-[150px] h-[150px] blur-[90px]">
                      <BorderWrapper wrapperClass="rounded-[18px] lg:rounded-[22px] h-full" className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[32px] lg:p-[44px] relative">
                        <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse" />
                        <Image src="/assets/illustrations/flyingcode-tech.svg" alt={t("story.whatWeStandFor")} width={96} height={96} className="mb-[24px] w-auto h-[76px] mix-blend-plus-lighter mx-auto" />
                        <h3 className="text-[22px] lg:text-[28px] font-semibold leading-[1.2] mb-[14px] text-center">{t("story.whatWeStandFor")}</h3>
                        <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 text-center">{t("story.whatWeStandForDescription")}</p>
                      </BorderWrapper>
                    </AnimatedCard>
                  </CarouselItem>
                  <CarouselItem className="pl-2 md:pl-[32px] basis-[85%] md:basis-3/4">
                    <AnimatedCard className="rounded-[18px] lg:rounded-[22px] h-full" circleClassName="w-[150px] h-[150px] blur-[90px]">
                      <BorderWrapper wrapperClass="rounded-[18px] lg:rounded-[22px] h-full" className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[32px] lg:p-[44px] relative">
                        <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#622aad]/20 rounded-full blur-[80px] pointer-events-none animate-shape-pulse" />
                        <Image src="/assets/illustrations/flyingcode-lightning.svg" alt={t("story.ourVision")} width={96} height={110} className="mb-[24px] w-auto h-[76px] mix-blend-plus-lighter mx-auto" />
                        <h3 className="text-[22px] lg:text-[28px] font-semibold leading-[1.2] mb-[14px] text-center">{t("story.ourVision")}</h3>
                        <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 text-center">{t("story.ourVisionDescription")}</p>
                      </BorderWrapper>
                    </AnimatedCard>
                  </CarouselItem>
                </CarouselContent>
                <div className="flex justify-center items-center mt-[30px] gap-[15px]">
                  <CarouselPrevious className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
                  <CarouselNext className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
                </div>
              </Carousel>
            </div>
            {/* DESKTOP GRID */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-[20px]">
              {/* Global Team card */}
              <motion.div variants={itemVariants}>
                <AnimatedCard
                  className="rounded-[18px] lg:rounded-[22px] h-full"
                  circleClassName="w-[150px] h-[150px] blur-[90px]"
                >
                  <BorderWrapper
                    wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                    className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[32px] lg:p-[44px] relative"
                  >
                    <div
                      className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                      />
                    <Image
                      src="/assets/illustrations/flyingcode-shield.svg"
                      alt="Global collaboration"
                      width={86}
                      height={111}
                      className="mb-[24px] w-auto h-[76px] mix-blend-plus-lighter mx-auto"
                      style={{ width: 'auto' }}
                    />
                    <h3 className="text-[22px] lg:text-[28px] font-semibold leading-[1.2] mb-[14px] text-center">
                      {t("story.globalTeamTitle")}
                    </h3>
                    <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 text-center">
                      {t("story.globalTeamDescription")}
                    </p>
                  </BorderWrapper>
                </AnimatedCard>
              </motion.div>

              {/* What We Stand For card */}
              <motion.div variants={itemVariants}>
                <AnimatedCard
                  className="rounded-[18px] lg:rounded-[22px] h-full"
                  circleClassName="w-[150px] h-[150px] blur-[90px]"
                >
                  <BorderWrapper
                    wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                    className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[32px] lg:p-[44px] relative"
                  >
                    <div
                      className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                      />
                    <Image
                      src="/assets/illustrations/flyingcode-tech.svg"
                      alt="What We Stand For"
                      width={96}
                      height={96}
                      className="mb-[24px] w-auto h-[76px] mix-blend-plus-lighter mx-auto"
                      style={{ width: 'auto' }}
                    />
                    <h3 className="text-[22px] lg:text-[28px] font-semibold leading-[1.2] mb-[14px] text-center">
                      {t("story.whatWeStandFor")}
                    </h3>
                    <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 text-center">
                      {t("story.whatWeStandForDescription")}
                    </p>
                  </BorderWrapper>
                </AnimatedCard>
              </motion.div>

              {/* Our Vision card */}
              <motion.div variants={itemVariants}>
                <AnimatedCard
                  className="rounded-[18px] lg:rounded-[22px] h-full"
                  circleClassName="w-[150px] h-[150px] blur-[90px]"
                >
                  <BorderWrapper
                    wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                    className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[32px] lg:p-[44px] relative"
                  >
                    <div
                      className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#622aad]/20 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                      />
                    <Image
                      src="/assets/illustrations/flyingcode-lightning.svg"
                      alt="Our Vision"
                      width={96}
                      height={110}
                      className="mb-[24px] w-auto h-[76px] mix-blend-plus-lighter mx-auto"
                      style={{ width: 'auto' }}
                    />
                    <h3 className="text-[22px] lg:text-[28px] font-semibold leading-[1.2] mb-[14px] text-center">
                      {t("story.ourVision")}
                    </h3>
                    <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 text-center">
                      {t("story.ourVisionDescription")}
                    </p>
                  </BorderWrapper>
                </AnimatedCard>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AboutStory;
